import { useEffect, useState } from 'react';
import {
  OAuthConnection,
  OrganizationsResponse,
  repositoryService,
} from '../../services/RepositoryService';
import { logger } from '../../utils/logger';
import { ProviderTab } from './types';

export interface UseOrganizationsDataParams {
  tokenInitialized: boolean;
  activeConnection: OAuthConnection | undefined;
  activeTab: ProviderTab;
  reloadRepositories: () => Promise<void>;
}

export function useOrganizationsData({
  tokenInitialized,
  activeConnection,
  activeTab,
  reloadRepositories,
}: UseOrganizationsDataParams): OrganizationsResponse | null {
  const [orgsData, setOrgsData] = useState<OrganizationsResponse | null>(null);

  useEffect(() => {
    if (!tokenInitialized || !activeConnection) {
      setOrgsData(null);
      return;
    }

    const loadOrgs = async () => {
      try {
        const data = await repositoryService.listOrganizations(activeTab);
        setOrgsData(data);
      } catch (err) {
        logger.error('[OAuth] Failed to load organizations', { error: err });
      }
    };

    loadOrgs();
  }, [tokenInitialized, activeConnection, activeTab]);

  useEffect(() => {
    const hasLimitedOrgs = orgsData?.organizations.some(
      (o) => o.accessStatus === 'restricted' || o.accessStatus === 'blocked',
    );
    if (!hasLimitedOrgs || !tokenInitialized || !activeConnection) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      try {
        const data = await repositoryService.listOrganizations(activeTab);
        const previouslyLimited =
          orgsData?.organizations
            .filter((o) => o.accessStatus === 'restricted' || o.accessStatus === 'blocked')
            .map((o) => o.login) || [];
        const nowActive = data.organizations.filter(
          (o) => o.accessStatus === 'active' && previouslyLimited.includes(o.login),
        );

        setOrgsData(data);

        if (nowActive.length > 0) {
          await repositoryService.syncProviderSafe(activeTab);
          await reloadRepositories();
        }
      } catch (err) {
        logger.error('[OAuth] Failed to refresh organizations on tab focus', { error: err });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [orgsData, tokenInitialized, activeConnection, activeTab, reloadRepositories]);

  return orgsData;
}
